import React from 'react';

const readStorage = <T>(keys: (keyof T)[]): T => {
  return keys.reduce((acc, key) => {
    const raw = localStorage.getItem(String(key));
    return {
      ...acc,
      [key]: raw ? JSON.parse(raw) : null,
    };
  }, {} as T);
};

// Similar to useLocalStorage from `@uidotdev/usehooks`, but for multiple keys
export const useLocaleStorage = <T>(keys: (keyof T)[]) => {
  const storeRef = React.useRef({
    snapshot: readStorage<T>(keys),
    listeners: new Set<() => void>(),
  });

  const subscribe = React.useCallback(
    (callback: () => void) => {
      storeRef.current.listeners.add(callback);

      const handler = (e: StorageEvent) => {
        if (e.key && keys.includes(e.key as keyof T)) {
          const newSnapshot = readStorage<T>(keys);
          const changed = keys.some(
            (k) => storeRef.current.snapshot[k] !== newSnapshot[k],
          );
          if (changed) {
            storeRef.current.snapshot = newSnapshot;
            storeRef.current.listeners.forEach((fn) => fn());
          }
        }
      };

      window.addEventListener('storage', handler);
      return () => {
        storeRef.current.listeners.delete(callback);
        window.removeEventListener('storage', handler);
      };
    },
    [keys],
  );

  const getSnapshot = React.useCallback(() => storeRef.current.snapshot, []);
  const getServerSnapshot = React.useCallback(
    () => storeRef.current.snapshot,
    [],
  );

  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
